import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from "react-router-dom";
import '../../Components/layout/SideBar.css';
import { isMasterRequestReviewer } from '../../pages/MasterRequest/masterRequestConfig';

function getReimbursementApprovalLabel(role) {
    const labels = {
        'Admin': 'Reimbursement Manager Approval',
        'Manager': 'Reimbursement Manager Approval',
        'Auditor': 'Reimbursement Auditor Approval'
    };
    return labels[role] || 'Reimbursement Approval';
}

// First path segment, lower-cased: "/MyServiceRequestDetail/5" -> "myservicerequestdetail"
function firstSegment(pathname) {
    return (pathname.split('/')[1] || '').toLowerCase();
}

// An item is active when the current route's first segment is one of its `match`
// segments, so detail / create pages highlight the list they belong to.
function isItemActive(item, segment) {
    const segments = item.match || [item.path.replace(/^\//, '')];
    return segments.some(s => s.toLowerCase() === segment);
}

function AppSidebar({ collapsed = false, onNavigate }) {
    const location = useLocation();
    const [userEmail] = useState(sessionStorage.getItem('username'));
    const [role] = useState(sessionStorage.getItem('role'));
    const [userrole] = useState(
        sessionStorage.getItem('userrole')
            ? sessionStorage.getItem('userrole').split(',').map(r => r.trim())
            : []
    );
    const canAccessOrderTracking = userrole.some(r => ['Admin', 'Coordinator', 'Manager'].includes(r));
    // Reuses the same helper as the route guard, so the menu can never offer a page
    // the guard then refuses. Either IsAdmin or Coordinator is enough.
    const canReviewMasterRequest = isMasterRequestReviewer();
    const isAdminOrManager = role === 'Admin' || role === 'Manager';

    // Visibility rules mirror the route guards in routes/router.js
    const sidebarSections = [
        {
            key: 'main',
            title: null, // top-level links, no group header
            items: [
                { path: "/", label: "Dashboard", icon: "feather-home", match: [''] },
                { path: "/Catalog", label: "Product Catalog", icon: "feather-grid", match: ['Catalog', 'Product'] },
                { path: "/Checkout", label: "Cart", icon: "feather-shopping-cart" },
                { path: "/OrderTracking", label: "Order Tracking", icon: "feather-truck", visible: canAccessOrderTracking }
            ]
        },
        {
            key: 'requests',
            title: "My Requests",
            icon: "feather-edit-3",
            items: [
                { path: "/MyRequest", label: "My Quotations", match: ['MyRequest', 'MyRequestDetail'] },
                { path: "/MyExpenseRequest", label: "My Reimbursements", match: ['MyExpenseRequest', 'MyExpenseRequestDetail', 'NewExpenseRequest'] },
                { path: "/MyServiceRequest", label: "My Service Requests", match: ['MyServiceRequest', 'MyServiceRequestDetail', 'NewServiceRequest'] },
                { path: "/MyMasterRequest", label: "My Customer/Item Requests", match: ['MyMasterRequest', 'MyMasterRequestDetail', 'NewMasterRequest'] }
            ]
        },
        {
            key: 'approvals',
            title: "Approvals",
            icon: "feather-check-square",
            items: [
                {
                    path: "/ExpenseApprovalList",
                    label: getReimbursementApprovalLabel(role),
                    match: ['ExpenseApprovalList', 'ExpenseApprovalDetail', 'ExpenseAdminApprovalDetail'],
                    visible: isAdminOrManager || role === 'Auditor'
                },
                {
                    path: "/ServiceRequestApprovalList",
                    label: "Service Request Approval",
                    match: ['ServiceRequestApprovalList', 'ServiceRequestApproverDetail'],
                    visible: userrole.some(r => ['Coordinator', 'Manager', 'MD', 'Commercial'].includes(r))
                },
                {
                    path: "/MasterRequestApprovalList",
                    label: "Customer/Item Requests",
                    match: ['MasterRequestApprovalList', 'MasterRequestApproverDetail'],
                    visible: canReviewMasterRequest
                }
            ]
        },
        {
            key: 'reports',
            title: "Reports",
            icon: "feather-bar-chart-2",
            items: [
                { path: "/ExpenseReport", label: "Reimbursement Report", visible: isAdminOrManager },
                { path: "/ServiceRequestReport", label: "Service Request Report", visible: isAdminOrManager },
                // Coordinator is a granular userrole value, not the single `role` field,
                // so the reviewer helper is what decides this one.
                { path: "/MasterRequestReport", label: "Customer/Item Request Report", visible: canReviewMasterRequest }
            ]
        },
        {
            key: 'admin',
            title: "Administration",
            icon: "feather-settings",
            items: [
                { path: "/ProductList", label: "Product Manager", match: ['ProductList', 'ManageProduct', 'ProductFiles', 'ProductImages'], visible: role === 'Admin' },
                { path: "/SpareManager", label: "Item Manager", visible: role === 'Admin' },
                { path: "/SparePublisher", label: "Item Publisher", visible: role === 'Admin' },
                { path: "/UserList", label: "User Manager", match: ['UserList', 'ManageUser'], visible: role === 'Admin' }
            ]
        }
    ]
        .map(section => ({ ...section, items: section.items.filter(item => item.visible !== false) }))
        .filter(section => section.items.length > 0);

    const segment = firstSegment(location.pathname);
    const activeGroupKey = sidebarSections.find(
        section => section.title && section.items.some(item => isItemActive(item, segment))
    )?.key ?? null;

    // Accordion: one group open at a time, following the active route
    const [openGroup, setOpenGroup] = useState(activeGroupKey);
    useEffect(() => {
        if (activeGroupKey) setOpenGroup(activeGroupKey);
    }, [activeGroupKey]);

    const toggleGroup = (key) => setOpenGroup(current => (current === key ? null : key));

    const renderItem = (item) => (
        <li key={item.path}>
            <NavLink
                to={item.path}
                end
                title={item.label}
                className={() => `sidebar-link${isItemActive(item, segment) ? ' active' : ''}`}
                aria-current={isItemActive(item, segment) ? 'page' : undefined}
                onClick={onNavigate}
            >
                {item.icon && <span className={`sidebar-icon ${item.icon}`} aria-hidden="true"></span>}
                <span className="sidebar-label">{item.label}</span>
            </NavLink>
        </li>
    );

    const renderSection = (section) => {
        if (!section.title) {
            return (
                <ul key={section.key} className="sidebar-list">
                    {section.items.map(renderItem)}
                </ul>
            );
        }

        const isOpen = openGroup === section.key;
        const hasActive = section.key === activeGroupKey;
        const panelId = `sidebar-group-${section.key}`;

        return (
            <div key={section.key} className={`sidebar-group${isOpen ? ' open' : ''}${hasActive ? ' has-active' : ''}`}>
                <button
                    type="button"
                    className="sidebar-group-toggle"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    title={section.title}
                    onClick={() => toggleGroup(section.key)}
                >
                    <span className={`sidebar-icon ${section.icon}`} aria-hidden="true"></span>
                    <span className="sidebar-label">{section.title}</span>
                    <span className="sidebar-chevron feather-chevron-down" aria-hidden="true"></span>
                </button>
                <div id={panelId} className="sidebar-group-panel">
                    <ul className="sidebar-list sidebar-sublist">
                        {section.items.map(renderItem)}
                    </ul>
                </div>
            </div>
        );
    };

    return (
        <nav
            id="sidebar-admin-wraper"
            className={`admin-sidebar${collapsed ? ' is-collapsed' : ''}`}
            aria-label="Main navigation"
        >
            <div className="sidebar-scroll">
                {sidebarSections.map(renderSection)}
            </div>

            <div className="sidebar-footer">
                <div className="sidebar-user" title={userEmail || ''}>
                    <span className="sidebar-avatar">{userEmail?.charAt(0).toUpperCase()}</span>
                    <span className="sidebar-user-meta sidebar-label">
                        <span className="sidebar-user-email">{userEmail}</span>
                        {role && <span className="sidebar-user-role">{role}</span>}
                    </span>
                </div>
                <ul className="sidebar-list">
                    {renderItem({ path: "/ChangePassword", label: "Change Password", icon: "feather-lock" })}
                </ul>
            </div>
        </nav>
    );
}

export default AppSidebar;
